/**
 * useCommandPaletteSearch Hook
 *
 * Encapsulates command palette search logic with:
 * - Multi-layer caching via SearchCacheService
 * - Stale-while-revalidate pattern
 * - Request cancellation via AbortController
 * - Smart endpoint selection based on query pattern
 * - Progressive result streaming
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Tag } from 'antd';
import { isArray } from 'lodash';
import { silentRequest } from '@/utils/http';
import { searchCache } from '@/services/search-cache';
import {
  intentDetailUrlParser,
  type InstructionsSearchResponse,
} from '@/services/instructions';
import {
  getSupportCenterList,
  type SupportCenterListItem,
  getSeverityColor,
} from '@/services/support-center';
import { flattenMenuItems } from '@/components/MegaMenu/megaMenuConfig';
import {
  isPermitted,
  ModuleName,
  PermissionVerbAction,
  type Acl,
} from '@/components/Guard';
import { formatSupportTicketReference } from '@/utils/supportTicketDisplay';

// ============================================================================
// Types
// ============================================================================

export type CommandPaletteResultType =
  | 'user'
  | 'instruction'
  | 'invoice'
  | 'card'
  | 'card_account'
  | 'wallet'
  | 'ticket'
  | 'page';

export type CommandPaletteResult = {
  id: string;
  type: CommandPaletteResultType;
  title: string;
  subtitle?: string;
  description?: string;
  path: string;
  icon?: React.ReactNode;
  tags?: React.ReactNode;
  avatar?: string;
  vendorName?: string;
};

export type GroupedResults = {
  pages: CommandPaletteResult[];
  users: CommandPaletteResult[];
  instructions: CommandPaletteResult[];
  invoices: CommandPaletteResult[];
  cards: CommandPaletteResult[];
  cardAccounts: CommandPaletteResult[];
  wallets: CommandPaletteResult[];
  tickets: CommandPaletteResult[];
};

type SearchOptions = {
  /** User's ACL for permission filtering */
  userAcl?: Acl;
  /** Whether support center is enabled */
  hasSupportCenter: boolean;
  /** List of enabled support center vendors */
  supportCenterVendors: Array<{ id: number; name?: string }>;
  /** Cache scope for the authenticated operator and support-center access. */
  ticketCacheScopeKey: string;
  /** Debounce wait time in ms (default: 150) */
  debounceMs?: number;
};

type SearchState = {
  results: GroupedResults;
  loading: boolean;
  /** True if results are from cache and being refreshed in background */
  isStale: boolean;
};

// ============================================================================
// Pattern Detection
// ============================================================================

const INSTRUCTION_PATTERN = /^(BP|GI|EX|FP|FI|CT|ES|ER|WT|DD|DW|FD|FW|TR)[A-Z0-9]{8}$/i;
const INVOICE_REF_PATTERN = /^IN[A-Z0-9]{8}$/i;
const CARD_LAST4_PATTERN = /^\d{4}$/;
const CARD_ACCOUNT_NUMBER_PATTERN = /^\d{3}-\d{3}-\d{7}-[A-Z0-9]{3,5}$/i;
const ACCOUNT_ID_PARTIAL_PATTERN = /^\d{5,}$/;
const EMAIL_PATTERN = /@/;
// Support Center ticket refs: IS0GLWWNN5, IS-0GLWWNN5, etc.
const TICKET_REF_PATTERN = /^IS-?(?:\d+|[A-Z0-9]{8})$/i;
/** Hyphenated client account / wallet-style numbers that may not match the strict card pattern */
const HYPHENATED_ACCOUNT_LIKE_PATTERN = /^\d{2,3}-\d{2,3}-\d{4,}-[A-Za-z0-9-]+$/i;

type QueryType =
  | 'instruction_ref'
  | 'invoice_ref'
  | 'ticket_ref'
  | 'card_last4'
  | 'card_account_number'
  | 'account_or_card'
  | 'email'
  | 'general';

function detectQueryType(query: string): QueryType {
  const trimmed = query.trim();
  
  if (INSTRUCTION_PATTERN.test(trimmed)) return 'instruction_ref';
  if (INVOICE_REF_PATTERN.test(trimmed)) return 'invoice_ref';
  if (TICKET_REF_PATTERN.test(trimmed)) return 'ticket_ref';
  if (CARD_LAST4_PATTERN.test(trimmed)) return 'card_last4';
  if (CARD_ACCOUNT_NUMBER_PATTERN.test(trimmed)) return 'card_account_number';
  if (ACCOUNT_ID_PARTIAL_PATTERN.test(trimmed)) return 'account_or_card';
  if (EMAIL_PATTERN.test(trimmed)) return 'email';
  return 'general';
}

// ============================================================================
// Status Color Helper
// ============================================================================

const STATUS_COLOR_MAP: Record<string, string> = {
  Submitted: 'blue',
  'In Progress': 'orange',
  'In Review': 'cyan',
  Resolved: 'green',
  Closed: 'default',
  Blocked: 'red',
  'Awaiting Your Response': 'gold',
  Scheduled: 'purple',
  ACTIVE: 'green',
  INACTIVE: 'default',
  BLOCKED: 'red',
  PENDING: 'orange',
  SUSPENDED: 'red',
  CLOSED: 'default',
  CANCELLED: 'default',
  EXPIRED: 'default',
  LOCKED: 'orange',
  PENDING_ACTIVATION: 'gold',
  PENDING_ISSUE: 'gold',
};

function getStatusColor(status: string): string {
  return STATUS_COLOR_MAP[status] || 'default';
}

// ============================================================================
// Empty Results
// ============================================================================

const EMPTY_RESULTS: GroupedResults = {
  pages: [],
  users: [],
  instructions: [],
  invoices: [],
  cards: [],
  cardAccounts: [],
  wallets: [],
  tickets: [],
};

// ============================================================================
// Hook Implementation
// ============================================================================

export function useCommandPaletteSearch(
  query: string,
  isOpen: boolean,
  options: SearchOptions
): SearchState & {
  menuItems: CommandPaletteResult[];
  flatResults: CommandPaletteResult[];
} {
  const {
    userAcl,
    hasSupportCenter,
    supportCenterVendors,
    ticketCacheScopeKey,
    debounceMs = 150,
  } = options;

  const canSearchWallets = useMemo(
    () => isPermitted(ModuleName.WALLET, PermissionVerbAction['VIEW:WALLET_ACCOUNTS'], userAcl),
    [userAcl]
  );

  const canSearchCardAccounts = useMemo(
    () => isPermitted(ModuleName.CMS, PermissionVerbAction['VIEW:CMS_CARD_ACCOUNT'], userAcl),
    [userAcl]
  );

  const canSearchSupportCenter = useMemo(
    () => isPermitted(ModuleName.SUPPORT_CENTER, PermissionVerbAction['VIEW:SUPPORT_CENTER'], userAcl),
    [userAcl]
  );

  // State
  const [results, setResults] = useState<GroupedResults>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [isStale, setIsStale] = useState(false);

  // Refs for abort and debounce
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchIdRef = useRef(0);

  // Build menu items from mega menu config, filtered by user permissions
  const menuItems = useMemo((): CommandPaletteResult[] => {
    return flattenMenuItems()
      .filter((item) => {
        if (!item.acl) return true;
        return isPermitted(
          item.acl.module as ModuleName,
          item.acl.action as PermissionVerbAction,
          userAcl
        );
      })
      .map((item) => ({
        id: item.id,
        type: 'page' as const,
        title: item.label,
        subtitle: item.parentLabel ? `${item.domain} → ${item.parentLabel}` : item.domain,
        path: item.path,
      }));
  }, [userAcl]);

  // ============================================================================
  // Search Fetchers
  // ============================================================================

  const fetchUsers = useCallback(
    async (searchQuery: string, signal: AbortSignal): Promise<CommandPaletteResult[]> => {
      const { data, fromCache } = await searchCache.getOrFetch(
        searchQuery,
        'users',
        async () => {
          const res = await silentRequest<{ data?: unknown[] } | unknown[]>('/users/general', {
            params: { searchParam: searchQuery, pageSize: 6 },
            signal,
          });
          const usersData = (res as { data?: unknown[] })?.data ?? res;
          if (!isArray(usersData)) return [];
          const lowerQuery = searchQuery.toLowerCase();
          // Map users with their accountNumber for sorting
          const mappedUsers = (usersData as Record<string, unknown>[]).slice(0, 6).map((user) => {
            const vendorName = (user.vendor as Record<string, unknown>)?.name as string | undefined;
            const userStatus = user.status as string | undefined;
            return {
              id: user.id as string,
              type: 'user' as const,
              title: (user.name || user.email || 'Unknown User') as string,
              subtitle: (user.accountNumber || user.accountId) as string | undefined,
              description: user.email as string | undefined,
              path: `/users/${user.id}`,
              vendorName,
              tags: (
                <>
                  {userStatus && userStatus !== 'ACTIVE' && (
                    <Tag color={getStatusColor(userStatus)}>{userStatus}</Tag>
                  )}
                  {vendorName && <Tag>{vendorName}</Tag>}
                </>
              ),
              _accountNumber: (user.accountNumber || '') as string,
            };
          });
          // Sort: prioritize account number matches over email matches
          mappedUsers.sort((a, b) => {
            const aAcctMatch = a._accountNumber.toLowerCase().includes(lowerQuery);
            const bAcctMatch = b._accountNumber.toLowerCase().includes(lowerQuery);
            if (aAcctMatch && !bAcctMatch) return -1;
            if (!aAcctMatch && bAcctMatch) return 1;
            return 0;
          });
          // Remove internal sorting field before returning
          return mappedUsers.map(({ _accountNumber, ...rest }) => rest);
        },
        signal
      );
      if (fromCache) setIsStale(true);
      return data;
    },
    []
  );

  const fetchCards = useCallback(
    async (searchQuery: string, signal: AbortSignal): Promise<CommandPaletteResult[]> => {
      const { data, fromCache } = await searchCache.getOrFetch(
        searchQuery,
        'cards',
        async () => {
          const res = await silentRequest<{ data?: unknown[] }>('/cms/cards/search/card', {
            params: { keyword: searchQuery },
            signal,
          });
          const cardData = (res as { data?: unknown[] })?.data;
          if (!cardData || !isArray(cardData)) return [];
          return (cardData as Record<string, unknown>[]).map((card) => ({
            id: card.id as string,
            type: 'card' as const,
            title: card.last4 ? `Card ****${card.last4}` : 'Card',
            subtitle: card.embossedName as string | undefined,
            description: [card.productName, card.userName, card.userAccountId]
              .filter(Boolean)
              .join(' · '),
            path: `/cms/cards/${card.id}/${card.userId}`,
            tags: card.status ? (
              <Tag color={getStatusColor(card.status as string)}>{card.status as string}</Tag>
            ) : null,
          }));
        },
        signal
      );
      if (fromCache) setIsStale(true);
      return data;
    },
    []
  );

  const fetchCardAccounts = useCallback(
    async (searchQuery: string, signal: AbortSignal): Promise<CommandPaletteResult[]> => {
      try {
        const { data, fromCache } = await searchCache.getOrFetch(
          searchQuery,
          'card_accounts',
          async () => {
            const res = await silentRequest<{ data?: unknown[]; total?: number }>('/cms/cards/card-accounts/list', {
              params: { cardAccountNumber: searchQuery, current: 1, pageSize: 10 },
              signal,
            });
            const rows = res?.data;
            if (!rows || !isArray(rows)) return [];
            const mapped: CommandPaletteResult[] = [];
            for (const row of rows as Record<string, unknown>[]) {
              const user = row.user as Record<string, unknown> | undefined;
              const userId = user?.id as string | undefined;
              const creditLineId = row.id as string | undefined;
              if (!userId || !creditLineId) continue;
              const asset = row.assetSymbol as string | undefined;
              mapped.push({
                id: creditLineId,
                type: 'card_account',
                title: (row.cardAccountLabel as string) || (row.label as string) || 'Card account',
                subtitle: (row.cardAccountNumber as string) || undefined,
                description: [user?.name, user?.email].filter(Boolean).join(' · '),
                path: `/cms/card-accounts/${creditLineId}/${userId}`,
                tags: asset ? <Tag>{asset}</Tag> : null,
              });
            }
            return mapped;
          },
          signal
        );
        if (fromCache) setIsStale(true);
        return data;
      } catch {
        return [];
      }
    },
    []
  );

  const fetchWallets = useCallback(
    async (searchQuery: string, signal: AbortSignal): Promise<CommandPaletteResult[]> => {
      try {
        const { data, fromCache } = await searchCache.getOrFetch(
          searchQuery,
          'wallets',
          async () => {
            const res = await silentRequest<unknown[] | { data?: unknown[] }>('/wallet/search', {
              params: { text: searchQuery, limit: 10 },
              signal,
            });
            const raw = Array.isArray(res) ? res : (res as { data?: unknown[] })?.data;
            if (!raw || !isArray(raw)) return [];
            const encodedSearch = encodeURIComponent(searchQuery);
            return (raw as Record<string, unknown>[]).map((w) => {
              const userId = w.userId as string | undefined;
              return {
                id: w.walletId as string,
                type: 'wallet' as const,
                title: (w.walletName as string) || 'Wallet',
                subtitle: w.walletAccountNumber as string | undefined,
                description: [w.name, w.email, w.vendorName].filter(Boolean).join(' · '),
                path: userId
                  ? `/users/${userId}?tab=wallet_accounts&walletAccountNumber=${encodedSearch}`
                  : `/client-account/wallet-accounts?tab=wallet_accounts&walletAccountNumber=${encodedSearch}`,
                vendorName: w.vendorName as string | undefined,
                tags: w.vendorName ? <Tag>{w.vendorName as string}</Tag> : null,
              };
            });
          },
          signal
        );
        if (fromCache) setIsStale(true);
        return data;
      } catch {
        return [];
      }
    },
    []
  );

  const fetchInstructions = useCallback(
    async (searchQuery: string, signal: AbortSignal): Promise<CommandPaletteResult[]> => {
      try {
        const { data, fromCache } = await searchCache.getOrFetch(
          searchQuery,
          'instructions',
          async () => {
            const res = await silentRequest<{ data?: unknown[] } | unknown[]>('/instructions/search', {
              params: { text: searchQuery },
              getResponse: true,
              signal,
            });
            const instructionData = (res as { data?: unknown[] })?.data ?? res;
            if (!isArray(instructionData)) return [];
            return (instructionData as InstructionsSearchResponse[]).slice(0, 5).map((instr) => {
              const url = intentDetailUrlParser({ type: instr.intent, refId: instr.refId });
              return {
                id: instr.intentId,
                type: 'instruction' as const,
                title: instr.refId,
                subtitle: instr.intent,
                path: url ? `${url}/${instr.intentId}` : '#',
                tags: <Tag color="blue">{instr.intent}</Tag>,
              };
            });
          },
          signal
        );
        if (fromCache) setIsStale(true);
        return data;
      } catch (error) {
        // Ignore SEARCH_PARAM_REQUIRED - this happens when the query doesn't match
        // the expected instruction pattern. Return empty results gracefully.
        const errorMessage = (error as Error)?.message || String(error);
        if (errorMessage.includes('SEARCH_PARAM_REQUIRED')) {
          return [];
        }
        // Re-throw other errors
        throw error;
      }
    },
    []
  );

  const fetchInvoices = useCallback(
    async (searchQuery: string, signal: AbortSignal): Promise<CommandPaletteResult[]> => {
      try {
        const { data, fromCache } = await searchCache.getOrFetch(
          searchQuery,
          'invoices',
          async () => {
            const res = await silentRequest<{ data?: unknown[]; total?: number }>('/invoices', {
              params: { refId: searchQuery.trim(), pageSize: 5 },
              signal,
            });
            const invoiceData = res?.data;
            if (!invoiceData || !isArray(invoiceData)) return [];
            return (invoiceData as Record<string, unknown>[]).slice(0, 5).map((invoice) => {
              const status = invoice.status as string | undefined;
              const userName = invoice.userName as string | undefined;
              return {
                id: invoice.id as string,
                type: 'invoice' as const,
                title: (invoice.refId as string) || 'Invoice',
                subtitle: userName || 'Invoice',
                description: invoice.details
                  ? `${((invoice.details as Record<string, unknown>).currency as string) || ''} ${((invoice.details as Record<string, unknown>).total as number)?.toLocaleString() || ''}`
                  : undefined,
                path: `/invoices?modal=details&id=${invoice.id as string}`,
                tags: status ? <Tag color={getStatusColor(status)}>{status}</Tag> : null,
              };
            });
          },
          signal
        );
        if (fromCache) setIsStale(true);
        return data;
      } catch {
        return [];
      }
    },
    []
  );

  const fetchTickets = useCallback(
    async (
      searchQuery: string,
      vendors: Array<{ id: number }>,
      signal: AbortSignal,
      isTicketReference = false,
      onPartialResults?: (tickets: CommandPaletteResult[]) => void
    ): Promise<CommandPaletteResult[]> => {
      const { data, fromCache } = await searchCache.getOrFetch(
        isTicketReference ? `ticket-ref:${ticketCacheScopeKey}:${searchQuery}` : searchQuery,
        'tickets',
        async () => {
          const allTickets: SupportCenterListItem[] = [];
          const fetchPromises = vendors.map(async (vendor) => {
            if (signal.aborted) return [];
            try {
              const result = await getSupportCenterList({
                ...(isTicketReference
                  ? { portalIssueReferenceId: searchQuery.replace(/^(IS)-/i, '$1') }
                  : { searchKeyword: searchQuery }),
                current: 1,
                pageSize: 3,
                vendorId: vendor.id,
              });
              return result?.issues && isArray(result.issues) ? result.issues : [];
            } catch {
              return [];
            }
          });

          const pending = fetchPromises.map((promise, index) => ({ promise, index }));
          while (pending.length > 0) {
            const completed = await Promise.race(
              pending.map(async ({ promise, index }) => ({ index, tickets: await promise }))
            );
            const completedIndex = pending.findIndex(({ index }) => index === completed.index);
            if (completedIndex >= 0) {
              pending.splice(completedIndex, 1);
            }
            allTickets.push(...completed.tickets);
            onPartialResults?.(mapTickets(allTickets));
          }

          return mapTickets(allTickets);

          function mapTickets(tickets: SupportCenterListItem[]): CommandPaletteResult[] {
            return tickets.slice(0, 3).map((ticket) => ({
              id: ticket.id,
              type: 'ticket' as const,
              title: formatSupportTicketReference(ticket) || ticket.id,
              subtitle: ticket.title || 'No title',
              path: `/support-center/all-requests/${ticket.id}`,
              tags: (
                <>
                  <Tag color={getStatusColor(ticket.status || 'Unknown')}>{ticket.status}</Tag>
                  {ticket.operatorAssignedSeverity && (
                    <Tag color={getSeverityColor(ticket.operatorAssignedSeverity)}>
                      {ticket.operatorAssignedSeverity}
                    </Tag>
                  )}
                </>
              ),
            }));
          }
        },
        signal
      );
      if (fromCache) setIsStale(true);
      return data;
    },
    [ticketCacheScopeKey]
  );

  // ============================================================================
  // Main Search Function
  // ============================================================================

  const performSearch = useCallback(
    async (searchQuery: string) => {
      const trimmed = searchQuery.trim();

      // Clear results for short queries
      if (!trimmed || trimmed.length < 2) {
        setResults(EMPTY_RESULTS);
        setLoading(false);
        setIsStale(false);
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const signal = controller.signal;

      // Track this search
      const currentSearchId = ++searchIdRef.current;
      const isStaleCheck = () => searchIdRef.current !== currentSearchId || signal.aborted;

      setLoading(true);
      setIsStale(false);

      // Immediately filter and show pages (synchronous)
      const lowerQuery = trimmed.toLowerCase();
      const pageResults = menuItems
        .filter(
          (item) =>
            item.title.toLowerCase().includes(lowerQuery) ||
            item.subtitle?.toLowerCase().includes(lowerQuery)
        )
        .slice(0, 10);

      // Reset remote result buckets so a new query cannot show stale rows from the previous one
      setResults({
        pages: pageResults,
        users: [],
        instructions: [],
        invoices: [],
        cards: [],
        cardAccounts: [],
        wallets: [],
        tickets: [],
      });

      // Detect query type for smart endpoint selection
      const queryType = detectQueryType(trimmed);

      try {
        const fetchPromises: Promise<void>[] = [];

        const scheduleWalletSearch = () => {
          if (!canSearchWallets) return;
          fetchPromises.push(
            fetchWallets(trimmed, signal).then((wallets) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, wallets }));
              }
            })
          );
        };

        const scheduleCardAccountSearch = () => {
          if (!canSearchCardAccounts) return;
          fetchPromises.push(
            fetchCardAccounts(trimmed, signal).then((cardAccounts) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, cardAccounts }));
              }
            })
          );
        };

        // Instruction ref - only search instructions
        if (queryType === 'instruction_ref') {
          fetchPromises.push(
            fetchInstructions(trimmed, signal).then((instructions) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, instructions }));
              }
            })
          );
        }
        // Invoice ref (IN-prefixed) - search invoices
        else if (queryType === 'invoice_ref') {
          fetchPromises.push(
            fetchInvoices(trimmed, signal).then((invoices) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, invoices }));
              }
            })
          );
        }
        // Support Center ticket ref (IS-prefixed) - prioritize ticket search
        else if (queryType === 'ticket_ref') {
          if (hasSupportCenter && canSearchSupportCenter) {
            fetchPromises.push(
              fetchTickets(trimmed, supportCenterVendors, signal, true, (tickets) => {
                if (!isStaleCheck()) {
                  setResults((prev) => ({ ...prev, tickets }));
                }
              }).then((tickets) => {
                if (!isStaleCheck()) {
                  setResults((prev) => ({ ...prev, tickets }));
                }
              })
            );
          }
        }
        // Card last4 - cards only (wallet search is too broad for 4 digits)
        else if (queryType === 'card_last4') {
          fetchPromises.push(
            fetchCards(trimmed, signal).then((cards) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, cards }));
              }
            })
          );
        }
        // Full card / wallet-style account number - cards and wallets in parallel
        else if (queryType === 'card_account_number') {
          fetchPromises.push(
            fetchCards(trimmed, signal).then((cards) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, cards }));
              }
            })
          );
          scheduleWalletSearch();
          scheduleCardAccountSearch();
        }
        // Account ID or partial card - search users, cards, wallets, and card accounts
        else if (queryType === 'account_or_card') {
          fetchPromises.push(
            fetchUsers(trimmed, signal).then((users) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, users }));
              }
            }),
            fetchCards(trimmed, signal).then((cards) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, cards }));
              }
            })
          );
          scheduleWalletSearch();
          scheduleCardAccountSearch();
        }
        // Email-like - only search users
        else if (queryType === 'email') {
          fetchPromises.push(
            fetchUsers(trimmed, signal).then((users) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, users }));
              }
            })
          );
        }
        // General search - users, tickets (instructions only for instruction_ref pattern)
        else {
          // Always search users
          fetchPromises.push(
            fetchUsers(trimmed, signal).then((users) => {
              if (!isStaleCheck()) {
                setResults((prev) => ({ ...prev, users }));
              }
            })
          );

          // Note: Instructions endpoint only works with valid instruction ref patterns
          // (e.g., BP12345678). For general text queries, it throws SEARCH_PARAM_REQUIRED.
          // So we only search instructions when queryType === 'instruction_ref' (handled above).

          // Tickets if support center enabled and query is 3+ chars
          if (hasSupportCenter && canSearchSupportCenter && trimmed.length >= 3) {
            fetchPromises.push(
              fetchTickets(trimmed, supportCenterVendors, signal).then((tickets) => {
                if (!isStaleCheck()) {
                  setResults((prev) => ({ ...prev, tickets }));
                }
              })
            );
          }

          if (HYPHENATED_ACCOUNT_LIKE_PATTERN.test(trimmed)) {
            scheduleWalletSearch();
            scheduleCardAccountSearch();
          }
        }

        // Wait for all fetches to complete
        await Promise.allSettled(fetchPromises);
      } catch (error) {
        // Ignore abort errors
        if ((error as Error)?.name !== 'AbortError') {
          console.error('[CommandPaletteSearch] Error:', error);
        }
      } finally {
        if (!isStaleCheck()) {
          setLoading(false);
          setIsStale(false);
        }
      }
    },
    [
      menuItems,
      fetchUsers,
      fetchCards,
      fetchCardAccounts,
      fetchWallets,
      fetchInstructions,
      fetchInvoices,
      fetchTickets,
      hasSupportCenter,
      canSearchSupportCenter,
      supportCenterVendors,
      canSearchWallets,
      canSearchCardAccounts,
    ]
  );

  // ============================================================================
  // Debounced Search Effect
  // ============================================================================

  useEffect(() => {
    if (!isOpen) {
      // Abort any in-flight requests when modal closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      return;
    }

    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If query is empty or too short, clear immediately
    if (!query.trim() || query.trim().length < 2) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      setIsStale(false);
      return;
    }

    // Set loading immediately for responsive UI
    setLoading(true);

    // Debounce the actual search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(query);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, isOpen, performSearch, debounceMs]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Reset when modal opens and prefetch recent searches
  useEffect(() => {
    if (isOpen) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      setIsStale(false);

      // Prefetch recent searches in background to warm cache
      const recentSearches = searchCache.getRecentSearches();
      if (recentSearches.length > 0) {
        // Create a temporary abort controller for prefetch
        const prefetchController = new AbortController();
        const signal = prefetchController.signal;

        // Prefetch top 3 recent searches (users only - most common use case)
        const prefetchPromises = recentSearches.slice(0, 3).map(async (recentQuery) => {
          // Skip if already cached
          const cached = searchCache.get(recentQuery, 'users');
          if (cached) return;

          try {
            await searchCache.getOrFetch(
              recentQuery,
              'users',
              async () => {
                const res = await silentRequest<{ data?: unknown[] } | unknown[]>('/users/general', {
                  params: { searchParam: recentQuery, pageSize: 6 },
                  signal,
                });
                const usersData = (res as { data?: unknown[] })?.data ?? res;
                if (!isArray(usersData)) return [];
                const lowerQuery = recentQuery.toLowerCase();
                // Map users with their accountNumber for sorting
                const mappedUsers = (usersData as Record<string, unknown>[]).slice(0, 6).map((user) => {
                  const vendorName = (user.vendor as Record<string, unknown>)?.name as string | undefined;
                  const userStatus = user.status as string | undefined;
                  return {
                    id: user.id as string,
                    type: 'user' as const,
                    title: (user.name || user.email || 'Unknown User') as string,
                    subtitle: (user.accountNumber || user.accountId) as string | undefined,
                    description: user.email as string | undefined,
                    path: `/users/${user.id}`,
                    vendorName,
                    tags: (
                      <>
                        {userStatus && userStatus !== 'ACTIVE' && (
                          <Tag color={getStatusColor(userStatus)}>{userStatus}</Tag>
                        )}
                        {vendorName && <Tag>{vendorName}</Tag>}
                      </>
                    ),
                    _accountNumber: (user.accountNumber || '') as string,
                  };
                });
                // Sort: prioritize account number matches over email matches
                mappedUsers.sort((a, b) => {
                  const aAcctMatch = a._accountNumber.toLowerCase().includes(lowerQuery);
                  const bAcctMatch = b._accountNumber.toLowerCase().includes(lowerQuery);
                  if (aAcctMatch && !bAcctMatch) return -1;
                  if (!aAcctMatch && bAcctMatch) return 1;
                  return 0;
                });
                // Remove internal sorting field before returning
                return mappedUsers.map(({ _accountNumber, ...rest }) => rest);
              },
              signal
            );
          } catch {
            // Silently ignore prefetch errors
          }
        });

        // Run prefetch in background - don't block
        Promise.allSettled(prefetchPromises);

        // Cleanup prefetch on modal close
        return () => {
          prefetchController.abort();
        };
      }
    }
    return undefined;
  }, [isOpen]);

  // Track successful searches for recent history
  useEffect(() => {
    if (!loading && query.trim().length >= 2) {
      const hasResults =
        results.users.length > 0 ||
        results.instructions.length > 0 ||
        results.invoices.length > 0 ||
        results.cards.length > 0 ||
        results.cardAccounts.length > 0 ||
        results.wallets.length > 0 ||
        results.tickets.length > 0;

      if (hasResults) {
        searchCache.addRecentSearch(query);
      }
    }
  }, [loading, query, results]);

  // ============================================================================
  // Flatten Results
  // ============================================================================

  const flatResults = useMemo(() => {
    if (!query.trim()) {
      // Show first 15 menu items as quick actions when no query
      return menuItems.slice(0, 15);
    }
    const all: CommandPaletteResult[] = [];
    all.push(...results.pages);
    all.push(...results.users);
    all.push(...results.instructions);
    all.push(...results.invoices);
    all.push(...results.cards);
    all.push(...results.cardAccounts);
    all.push(...results.wallets);
    all.push(...results.tickets);
    return all;
  }, [query, results, menuItems]);

  return {
    results,
    loading,
    isStale,
    menuItems,
    flatResults,
  };
}

export default useCommandPaletteSearch;
